import { USER_ID_KEY } from "@medme/auth"
import {
	Avatar,
	AvatarFallback,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Skeleton,
} from "@medme/ui"
import { useNavigate } from "@tanstack/react-router"
import { ChevronsUpDown, User } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/entities/auth"
import { usePlatformUserList } from "@/entities/platform-user/use-platform-users"

const NAME_SEPARATOR = " "

function readCurrentUserId() {
	if (typeof window === "undefined") return ""
	return localStorage.getItem(USER_ID_KEY) ?? ""
}

function initialsFromName(name: string) {
	return name
		.split(NAME_SEPARATOR)
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2)
}

export function SidebarUserProfile() {
	const { t } = useTranslation()
	const { logout } = useAuth()
	const navigate = useNavigate()
	const list = usePlatformUserList()
	const currentUserId = readCurrentUserId()
	const current =
		list.data?.self ??
		list.data?.items.find((user) => user.id === currentUserId)
	const initials = current?.name ? initialsFromName(current.name) : ""

	return (
		<div className="flex items-center justify-between p-1">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<div className="flex items-center gap-2 min-w-0 flex-1 hover:bg-accent rounded-md py-1">
						<Avatar>
							<AvatarFallback>
								{initials || <User className="size-4" aria-hidden="true" />}
							</AvatarFallback>
						</Avatar>
						{list.isLoading ? (
							<div className="flex flex-col gap-2 w-full">
								<Skeleton className="p-1" />
								<Skeleton className="p-1" />
							</div>
						) : (
							<div className="flex flex-col min-w-0">
								<span className="font-semibold text-sm truncate">
									{current?.name}
								</span>
								{current?.email ? (
									<span className="text-xs text-muted-foreground truncate">
										{current.email}
									</span>
								) : null}
							</div>
						)}
						<button
							type="button"
							className="p-1 ml-auto"
							aria-label="User menu"
						>
							<ChevronsUpDown className="size-4" aria-hidden="true" />
						</button>
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem
						onClick={() => {
							logout()
							navigate({ to: "/", replace: true })
						}}
					>
						{t("sidebar.user-profile.logout")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}
